import { Sparkles, Plus, AlertTriangle, Phone, CreditCard, Gauge } from 'lucide-react';
import { typedApi } from '../../api/typed';
import { useResource } from '../../hooks/useResource';
import { useTimezone } from '../../contexts/TimezoneContext';

export default function RealitySnapshot({
  summary, activeTasks, completedTasks, user, planningDay,
  onPlanDay, onNewTask, urgentCount, todayCalls, overduePayments,
  businessPressure, activeBusiness, onBusinessFilter
}) {
  const { nowInTz } = useTimezone();
  const hour = nowInTz().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.name?.split(' ')[0] || 'there';
  const phase = hour < 10 ? 'Plan' : hour < 16 ? 'Execute' : 'Review';

  const { data: velocity } = useResource(
    'reality-snapshot-velocity',
    async ({ signal }) => await typedApi.get('/api/analytics/velocity', { signal }),
    { initialData: null },
  );

  const riskLevel = summary?.riskLevel || 'LOW';
  const total = activeTasks + completedTasks;
  const completionPct = total > 0 ? Math.round(completedTasks / total * 100) : 0;

  const avgH = velocity?.avgDecisionHours || 0;
  const velocityLabel = avgH < 1 ? `${Math.round(avgH * 60)}m` : avgH < 24 ? `${avgH}h` : `${Math.round(avgH / 24)}d`;

  function riskColor(level) {
    if (level === 'HIGH') return 'var(--pd-crimson)';
    if (level === 'MEDIUM') return 'var(--pd-warning)';
    return 'var(--pd-focus)';
  }
  function velocityColor(hours) {
    if (hours < 4) return 'var(--pd-success)';
    if (hours < 12) return 'var(--pd-warning)';
    return 'var(--pd-crimson)';
  }
  const velocityTone = velocityColor(avgH);

  return (
    <div data-testid="reality-snapshot" className="px-4 py-2"
      style={{ background: 'var(--pd-bg)', borderBottom: '1px solid var(--pd-border-subtle)' }}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative flex-shrink-0">
            <svg width="34" height="34" className="-rotate-90">
              <circle cx="17" cy="17" r="14" fill="none" stroke="var(--pd-border)" strokeWidth="2.5" />
              <circle cx="17" cy="17" r="14" fill="none"
                stroke={riskColor(riskLevel)}
                strokeWidth="2.5" strokeDasharray={2 * Math.PI * 14} strokeDashoffset={2 * Math.PI * 14 * (1 - completionPct / 100)}
                strokeLinecap="round" className="progress-ring-circle" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[9px] font-bold tabular-nums" style={{ color: 'var(--pd-text)' }}>{completionPct}%</span>
            </div>
          </div>

          <div className="min-w-0 flex items-center gap-2 flex-wrap">
            <span className="text-[9px] uppercase tracking-[0.12em] font-semibold px-1.5 py-0.5 rounded"
              style={{ background: 'var(--pd-panel-2)', color: 'var(--pd-subtle)' }}>{phase}</span>
            <h2 className="text-[13px] font-semibold truncate" style={{ color: 'var(--pd-text)', letterSpacing: '-0.02em' }}>
              {greeting}, {firstName}
            </h2>
            <div className="flex items-center gap-2 text-[10px]" style={{ color: 'var(--pd-muted)' }}>
              {velocity && velocity.totalDecisions > 0 && (
                <span data-testid="decision-velocity-metric" className="flex items-center gap-1 font-semibold"
                  title="Average decision turnaround"
                  style={{ color: velocityTone }}>
                  <Gauge className="w-3 h-3" />{velocityLabel}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
          {urgentCount > 0 && (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
              style={{ background: 'var(--pd-crimson-soft)', color: 'var(--pd-crimson)' }}>
              <AlertTriangle className="w-3 h-3" />{urgentCount} urgent
            </div>
          )}
          {todayCalls > 0 && (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
              style={{ background: 'var(--pd-blue-soft)', color: 'var(--pd-focus)' }}>
              <Phone className="w-3 h-3" />{todayCalls} calls
            </div>
          )}
          {overduePayments > 0 && (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
              style={{ background: 'rgba(180,83,9,0.08)', color: 'var(--pd-warning)' }}>
              <CreditCard className="w-3 h-3" />{overduePayments} overdue
            </div>
          )}
          <button data-testid="plan-my-day-button" onClick={onPlanDay} disabled={planningDay}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold text-white btn-primary pd-btn-press"
            style={{ opacity: planningDay ? 0.7 : 1 }}>
            <Sparkles className="w-3 h-3" />{planningDay ? 'Planning…' : 'Plan day'}
          </button>
          <button data-testid="new-task-button" onClick={onNewTask}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-medium btn-ghost pd-btn-press">
            <Plus className="w-3 h-3" />New
          </button>
        </div>
      </div>

      {/* Business Summary Strip */}
      {businessPressure && businessPressure.length > 0 && (
        <div data-testid="business-summary-strip" className="pd-scroll-strip flex items-center gap-1.5 mt-2.5 pb-1">
          <button
            data-testid="biz-summary-all"
            onClick={() => onBusinessFilter?.(null)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-colors flex-shrink-0 pd-btn-press"
            style={{
              background: !activeBusiness ? 'var(--pd-panel-2)' : 'transparent',
              color: !activeBusiness ? 'var(--pd-text)' : 'var(--pd-subtle)',
              border: `1px solid ${!activeBusiness ? 'var(--pd-border)' : 'transparent'}`,
            }}
          >
            All
          </button>
          {businessPressure.map(biz => {
            const isActive = activeBusiness?.id === biz.id;
            const statusParts = [];
            if (biz.urgent > 0) statusParts.push(`${biz.urgent} urgent`);
            else if (biz.tasks > 0) statusParts.push(`${biz.tasks}`);
            if (biz.calls > 0) statusParts.push(`${biz.calls} calls`);
            if (biz.payments > 0) statusParts.push(`${biz.payments} pay`);
            const statusText = statusParts.length > 0 ? statusParts.join(' · ') : '';
            const hasUrgent = biz.urgent > 0;

            return (
              <button
                key={biz.id}
                data-testid={`biz-summary-${(biz.shortCode || '').toLowerCase()}`}
                onClick={() => onBusinessFilter?.(isActive ? null : biz)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-colors flex-shrink-0 pd-btn-press"
                style={{
                  background: isActive ? `${biz.color}14` : 'transparent',
                  color: isActive ? 'var(--pd-text)' : 'var(--pd-muted)',
                  border: `1px solid ${isActive ? `${biz.color}40` : 'transparent'}`,
                }}
              >
                <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: biz.color }} />
                <span className="font-semibold">{biz.shortCode}</span>
                {statusText && <span style={{ color: hasUrgent ? 'var(--pd-crimson)' : 'var(--pd-subtle)' }}>{statusText}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
